<template>
  <main class="db-login">
    <div class="login-card">
      <img
        class="login-logo"
        :src="$store.getters.isDarkMode ? require('@/assets/img/logo_santo_domingo_dark.png') : require('@/assets/img/logo_santo_domingo.png')"
        :alt="$root.constantes.logo_nombre_sitio"
      />

      <header class="login-head">
        <h1 class="login-title">Panel de control</h1>
        <p class="login-subtitle">Introduce tus credenciales para acceder.</p>
      </header>

      <p v-if="aviso" class="login-notice" role="status">
        <span class="material-symbols-outlined">info</span>
        {{ aviso }}
      </p>

      <form class="db-form" novalidate @submit.prevent="login">
        <div class="db-field">
          <label class="db-label" for="email">Correo electrónico</label>
          <div class="login-input">
            <span class="material-symbols-outlined">mail</span>
            <input
              id="email"
              v-model.trim="user"
              type="email"
              class="db-input"
              placeholder="nombre@ejemplo.com"
              autocomplete="username"
              required
              autofocus
            />
          </div>
        </div>

        <div class="db-field">
          <label class="db-label" for="password">Contraseña</label>
          <div class="login-input">
            <span class="material-symbols-outlined">lock</span>
            <input
              id="password"
              v-model="password"
              :type="verPassword ? 'text' : 'password'"
              class="db-input"
              placeholder="••••••••"
              autocomplete="current-password"
              required
            />
            <button
              type="button"
              class="login-eye"
              :aria-label="verPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
              @click="verPassword = !verPassword"
            >
              <span class="material-symbols-outlined">{{ verPassword ? "visibility_off" : "visibility" }}</span>
            </button>
          </div>
        </div>

        <p v-if="error" class="login-error" role="alert">
          <span class="material-symbols-outlined">error</span>
          {{ error }}
        </p>

        <button type="submit" class="db-btn db-btn--block login-submit" :disabled="loginDisabled">
          <span v-if="loading" class="db-spinner"></span>
          <template v-else-if="espera > 0">Espera {{ esperaTexto }}</template>
          <template v-else>Iniciar sesión</template>
        </button>
      </form>

      <router-link :to="{ name: 'inicio' }" class="login-back">
        <span class="material-symbols-outlined">arrow_back</span> Volver a la web
      </router-link>
    </div>
  </main>
</template>

<script>
import axios, { MOTIVO_LOGIN_KEY } from "@/directives/axios.js";
import { mensajeError } from "@/directives/dbDialogs.js";

const AVISOS = {
  caducada: "Tu sesión ha caducado o se ha cerrado desde otro dispositivo. Vuelve a iniciar sesión.",
  bloqueada: "Tu cuenta ha sido bloqueada. Si crees que es un error, contacta con un administrador.",
};

export default {
  name: "LoginView",

  metaInfo: {
    title: "Acceso al panel",
  },

  data() {
    return {
      loading: false,
      user: "",
      password: "",
      verPassword: false,
      error: "",
      aviso: "",
      espera: 0,
      temporizador: null,
    };
  },

  computed: {
    loginDisabled() {
      return this.loading || this.espera > 0 || !this.user || !this.password;
    },
    esperaTexto() {
      const m = Math.floor(this.espera / 60);
      const s = String(this.espera % 60).padStart(2, "0");
      return m ? `${m}:${s} min` : `${this.espera} s`;
    },
  },

  watch: {
    user() {
      this.error = "";
    },
    password() {
      this.error = "";
    },
  },

  methods: {
    async login() {
      if (this.loginDisabled) {
        return;
      }

      this.loading = true;
      this.error = "";

      try {
        const response = await axios.post("/login", {
          email: this.user,
          password: this.password,
        });

        if (!response.data || !response.data.token) {
          throw new Error("El servidor no devolvió un token de acceso.");
        }

        sessionStorage.setItem("token_parmada", response.data.token);
        // El login ya trae el usuario con sus permisos: el panel arranca sin esperar a /perfil.
        this.$store.commit("setUsuario", response.data.usuario || null);
        await this.$router.replace({ name: "dashboard" });
      } catch (error) {
        const data = (error.response && error.response.data) || {};
        if (error.response && error.response.status === 429) {
          this.iniciarEspera(Number(data.retry_after_seconds) || 60);
          this.error = mensajeError(error, "Demasiados intentos. Espera un momento.");
        } else {
          this.error = error.response
            ? mensajeError(error, "Usuario o contraseña incorrectos.")
            : "Error de conexión. Inténtalo de nuevo.";
        }
      } finally {
        this.loading = false;
      }
    },

    iniciarEspera(segundos) {
      clearInterval(this.temporizador);
      this.espera = segundos;
      this.temporizador = setInterval(() => {
        this.espera -= 1;
        if (this.espera <= 0) {
          clearInterval(this.temporizador);
          this.espera = 0;
          this.error = "";
        }
      }, 1000);
    },
  },

  created() {
    if (sessionStorage.getItem("token_parmada")) {
      this.$router.replace({ name: "dashboard" });
      return;
    }
    const motivo = sessionStorage.getItem(MOTIVO_LOGIN_KEY);
    if (motivo) {
      this.aviso = AVISOS[motivo] || "";
      sessionStorage.removeItem(MOTIVO_LOGIN_KEY);
    }
  },

  beforeDestroy() {
    clearInterval(this.temporizador);
  },
};
</script>

<style scoped>
.login-card {
  width: 100%;
  max-width: 400px;
  padding: 36px 32px 28px;
  border: 1px solid var(--sd-border);
  border-radius: 22px;
  background: var(--sd-surface);
  box-shadow: var(--sd-shadow-lg);
}

.login-logo {
  display: block;
  width: 240px;
  max-width: 100%;
  margin: 0 auto 24px;
}

.login-head {
  margin-bottom: 24px;
  text-align: center;
}

.login-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--sd-text);
}

.login-subtitle {
  margin: 6px 0 0;
  font-size: 0.9rem;
  color: var(--sd-muted);
}

.login-input {
  position: relative;
}

.login-input > .material-symbols-outlined {
  position: absolute;
  top: 50%;
  left: 12px;
  color: var(--sd-muted);
  transform: translateY(-50%);
  pointer-events: none;
}

.login-input .db-input {
  height: 46px;
  padding-left: 42px;
}

.login-eye {
  position: absolute;
  top: 50%;
  right: 6px;
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--sd-muted);
  cursor: pointer;
  transform: translateY(-50%);
}

.login-eye:hover {
  color: var(--sd-text);
}

.login-input:has(.login-eye) .db-input {
  padding-right: 44px;
}

.login-error {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--sd-danger-soft);
  font-size: 0.88rem;
  color: var(--sd-danger);
}

.login-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 18px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--sd-warning-soft);
  font-size: 0.88rem;
  color: var(--sd-warning);
}

.login-submit {
  height: 46px;
  font-size: 0.95rem;
}

.login-back {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 20px;
  font-size: 0.85rem;
  color: var(--sd-muted);
  text-decoration: none;
}

.login-back:hover {
  color: var(--sd-primary);
  text-decoration: none;
}

.login-back .material-symbols-outlined {
  font-size: 18px;
}
</style>
